import React, { ChangeEventHandler } from "react";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import { lang } from "@lang/interfaces/lang";
import InputComponent from "@ui/components/Input/Input.component";
import useDictionary from "@ui/hooks/useDictionary";
import moment from "moment";
import { Alert } from "@mui/material";

interface IProps {
    lang: lang;
    periodicActiveTime: number | null;
    periodicActiveTimeError: boolean;
    periodicSleepDays: number | null;
    periodicSleepDaysError: boolean;
    periodicSleepHours: number | null;
    periodicSleepHoursError: boolean;
    periodicSleepMinutes: number | null;
    periodicSleepMinutesError: boolean;
    periodicLocationPeriod: number | null;
    periodicLocationPeriodError: boolean;
    onPeriodicActiveTimeChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepDaysChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepHoursChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicSleepMinutesChange: ChangeEventHandler<HTMLInputElement>;
    onPeriodicLocationPeriodChange: ChangeEventHandler<HTMLInputElement>;
    onInputClick: () => void;
}

const Component: React.FC<IProps> = ({
    lang,
    periodicActiveTime,
    periodicActiveTimeError,
    periodicSleepDays,
    periodicSleepDaysError,
    periodicSleepHours,
    periodicSleepHoursError,
    periodicSleepMinutes,
    periodicSleepMinutesError,
    periodicLocationPeriod,
    periodicLocationPeriodError,
    onPeriodicActiveTimeChange,
    onPeriodicSleepDaysChange,
    onPeriodicSleepHoursChange,
    onPeriodicSleepMinutesChange,
    onPeriodicLocationPeriodChange,
    onInputClick,
}: IProps) => {
    const getWord = useDictionary();

    const currentPeriodic = moment()
        .add(periodicSleepDays, "days")
        .add(periodicSleepHours, "hours")
        .add(periodicSleepMinutes, "minutes");

    const isTooMuchPeriod = currentPeriodic > moment().add(1, "day");

    return (
        <>
            <InputComponent
                label="periodicActiveTime"
                name="period"
                type="number"
                description="periodicActiveTimeTooltip"
                helperText="periodicActiveTimeHelper"
                errorMessage="periodicActiveTimeError"
                lang={lang}
                value={periodicActiveTime}
                numberProps={{
                    min: 3,
                    max: 15,
                }}
                error={periodicActiveTimeError}
                onChange={onPeriodicActiveTimeChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
            <Typography className="device-settings__item-title">
                {`${getWord("periodicSleepTime")}:`}
            </Typography>
            <Box className="device-settings__sleep-time-wrap">
                <InputComponent
                    label="periodicSleepTimeDays"
                    name="period"
                    type="number"
                    description="periodicSleepTimeDaysTooltip"
                    helperText="periodicSleepTimeDaysHelper"
                    errorMessage="periodicSleepDaysError"
                    lang={lang}
                    value={periodicSleepDays}
                    numberProps={{
                        min: 0,
                        max: 30,
                    }}
                    error={periodicSleepDaysError}
                    onChange={onPeriodicSleepDaysChange}
                    onClick={onInputClick}
                    onFocus={onInputClick}
                />
                <InputComponent
                    label="periodicSleepTimeHours"
                    name="period"
                    type="number"
                    description="periodicSleepTimeHoursTooltip"
                    helperText="periodicSleepTimeHoursHelper"
                    errorMessage="periodicSleepHoursError"
                    lang={lang}
                    value={periodicSleepHours}
                    numberProps={{
                        min: 0,
                        max: 23,
                    }}
                    error={periodicSleepHoursError}
                    onChange={onPeriodicSleepHoursChange}
                    onClick={onInputClick}
                    onFocus={onInputClick}
                />
                <InputComponent
                    label="periodicSleepTimeMinutes"
                    name="period"
                    type="number"
                    description="periodicSleepTimeMinutesTooltip"
                    helperText="periodicSleepTimeMinutesHelper"
                    errorMessage="periodicSleepMinutesError"
                    lang={lang}
                    value={periodicSleepMinutes}
                    numberProps={{
                        min: 1,
                        max: 59,
                    }}
                    error={periodicSleepMinutesError}
                    onChange={onPeriodicSleepMinutesChange}
                    onClick={onInputClick}
                    onFocus={onInputClick}
                />
                {isTooMuchPeriod && (
                    <Alert className="device-settings__periodic-alert" severity="warning">
                        {getWord('longPeriodAttention')}
                    </Alert>
                )}
            </Box>
            <InputComponent
                label="periodicLocationPeriod"
                name="period"
                type="number"
                description="periodicLocationPeriodHelper"
                errorMessage="periodicLocationPeriodError"
                lang={lang}
                value={periodicLocationPeriod}
                numberProps={{
                    min: 1,
                    max: 1440,
                }}
                error={periodicLocationPeriodError}
                onChange={onPeriodicLocationPeriodChange}
                onClick={onInputClick}
                onFocus={onInputClick}
            />
        </>
    );
};

const PeriodicSettingsComponent = React.memo(Component);

export default PeriodicSettingsComponent;
